/**
 * Ext class picker: same per-lesson location-filter interaction as
 * rrr-class-picker, but dropdowns are optional and the whole thing adds
 * to the cart as ONE line item at quantity 1. Since a Shopify line's
 * price is always variant price x quantity, a single quantity-1 line
 * can't total the sum of several different variants on its own - so
 * instead this swaps the form's variant `id` to whichever "N Lessons"
 * tier variant matches how many lessons were picked (see
 * ext-class-picker.liquid's data-tier-variants, built from the product's
 * own variants at render time). That tier variant carries the real,
 * already-correct price for that lesson count.
 *
 * No custom fetch/add-to-cart code here: each visible dropdown has a
 * hidden, form-associated proxy input that mirrors its value and is only
 * enabled when filled, and the hidden `id` input gets its value swapped
 * to the matching tier variant - native submission (product-form.js's
 * own FormData(form) read) handles the rest.
 */
class ExtClassPicker extends HTMLElement {
  #boundSubmit = this.#onFormSubmit.bind(this);
  /** @type {Record<string, {id: string, price: string}>} */
  #tierVariants = {};

  connectedCallback() {
    try {
      this.#tierVariants = JSON.parse(this.dataset.tierVariants || '{}');
    } catch {
      this.#tierVariants = {};
    }

    this.#bindLessonLocationFilters();
    this.#bindDropdowns();

    const form = this.#getProductForm();
    if (!form) return;

    this.#syncAll(form);

    const scope = form.closest('.product-information, .shopify-section') || document;
    scope.addEventListener('input', () => this.#syncAll(form));
    scope.addEventListener('change', () => this.#syncAll(form));

    form.addEventListener('submit', this.#boundSubmit, true);
  }

  disconnectedCallback() {
    this.#getProductForm()?.removeEventListener('submit', this.#boundSubmit, true);
  }

  #bindLessonLocationFilters() {
    this.querySelectorAll('[data-ext-lesson-location-filter]').forEach((filter) => {
      if (!(filter instanceof HTMLSelectElement)) return;

      const field = filter.closest('.ext-class-picker__field');
      const dropdown = field?.querySelector('[data-ext-dropdown]');
      if (!(dropdown instanceof HTMLSelectElement)) return;

      filter.addEventListener('change', () => {
        this.#applyLocationFilter(dropdown, filter.value);
      });
    });
  }

  #bindDropdowns() {
    this.querySelectorAll('[data-ext-dropdown]').forEach((dropdown) => {
      if (!(dropdown instanceof HTMLSelectElement)) return;
      dropdown.addEventListener('change', () => this.#syncProxy(dropdown));
    });
  }

  /**
   * @param {HTMLSelectElement} dropdown
   * @param {string} location
   */
  #applyLocationFilter(dropdown, location) {
    let selectedOptionHidden = false;

    [...dropdown.options].forEach((option) => {
      if (!option.value) return; // the "None" option
      const optionLocation = option.dataset.extLocation || '';
      const matches = location === '' || optionLocation === '' || optionLocation === location;
      option.hidden = !matches;
      option.disabled = !matches;
      if (!matches && option.selected) selectedOptionHidden = true;
    });

    if (selectedOptionHidden) dropdown.value = '';

    this.#syncProxy(dropdown);
  }

  /**
   * Mirrors one dropdown's value onto its hidden proxy input, enabling
   * the proxy only when the dropdown has a real selection.
   * @param {HTMLSelectElement} dropdown
   */
  #syncProxy(dropdown) {
    const field = dropdown.closest('.ext-class-picker__field');
    const proxy = field?.querySelector('[data-ext-proxy]');
    if (!(proxy instanceof HTMLInputElement)) return;

    proxy.value = dropdown.value;
    proxy.disabled = dropdown.value === '';

    const form = this.#getProductForm();
    if (form) this.#syncAll(form);
  }

  /** @returns {HTMLSelectElement[]} */
  #selectedDropdowns() {
    return /** @type {HTMLSelectElement[]} */ ([...this.querySelectorAll('[data-ext-dropdown]')]).filter(
      (select) => select instanceof HTMLSelectElement && select.value
    );
  }

  /**
   * @param {HTMLFormElement} form
   */
  #syncAll(form) {
    const count = this.#selectedDropdowns().length;
    // Before any lesson is picked, show the "1 Lesson" tier as a sensible
    // starting price instead of whatever the product's raw default
    // variant happens to be - the button stays disabled either way since
    // that only depends on `count > 0` below.
    const displayTier = this.#tierVariants[String(count)] || this.#tierVariants['1'];

    const idInput = form.querySelector('input[name="id"]');
    if (idInput instanceof HTMLInputElement && displayTier) {
      idInput.value = displayTier.id;
    }
    if (displayTier) this.#updatePriceDisplay(displayTier.price);

    const ready = count > 0 && !!this.#tierVariants[String(count)] && form.checkValidity();
    const scope = form.closest('.product-information, .shopify-section') || document;
    const addButtons = scope.querySelectorAll('[ref="addToCartButton"], .add-to-cart-button');
    addButtons?.forEach((btn) => {
      if (btn instanceof HTMLButtonElement) btn.disabled = !ready;
    });
  }

  /**
   * The theme's price block doesn't react to us swapping the hidden id
   * input on its own, so update its text directly. snippets/price.liquid
   * renders the actual VISIBLE price as a plain <span class="price">
   * (both the .price__regular one and the hidden .price-item--sale one
   * carry this class; the struck-through compare-at-price span does
   * NOT, so it's naturally left alone here).
   *
   * Scoped to THIS product's own <product-price> block via
   * data-product-id - the page also has other products' price displays
   * (e.g. "You may also like" cards) that must not be touched. The
   * mobile sticky bar is safe to update unscoped since there's only
   * ever one, always for the current product.
   * @param {string} formatted
   */
  #updatePriceDisplay(formatted) {
    const productId = this.dataset.extProductId;
    if (productId) {
      document.querySelectorAll(`product-price[data-product-id="${productId}"] .price`).forEach((el) => {
        el.textContent = formatted;
      });
    }

    document.querySelectorAll('.sticky-add-to-cart__price .price').forEach((el) => {
      el.textContent = formatted;
    });
  }

  /**
   * Safety net: blocks submission if it somehow fires with nothing
   * selected, or with a count that has no matching tier variant set up
   * (e.g. admin hasn't created a "3 Lessons" variant yet).
   * @param {SubmitEvent} event
   */
  #onFormSubmit(event) {
    const count = this.#selectedDropdowns().length;
    if (count > 0 && this.#tierVariants[String(count)]) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const form = this.#getProductForm();
    form?.reportValidity();
  }

  #getProductForm() {
    const formId = this.dataset.formId;
    const form = formId ? document.getElementById(formId) : null;
    return form instanceof HTMLFormElement ? form : null;
  }
}

if (!customElements.get('ext-class-picker')) {
  customElements.define('ext-class-picker', ExtClassPicker);
}
